/**
 * Pack-desk snapshot restore, shared by the flagged-resolve screen and the
 * dispatch desk's shipping-label reopen.
 */
import type { DispatchBox, DispatchOrder, FlaggedPackState } from "../../types/Dispatch";

// What a server-side reopen hands back to the desk that asked for it.
// Resolving to null (rather than this) is the failure signal: the order is
// NOT safe to pack, so the caller must not open it.
export type ReopenResult = {
    // The pack desk as the packer left it, rebuilt from the parcels the
    // reopen deleted. Null when the order held none (nothing to restore).
    packState: FlaggedPackState | null;
};

// Re-hydrate the pack desk from a pack-state snapshot — the counts, the
// boxes, and which units were scanned into which box — so an operator
// picks up exactly where the packer stopped instead of rescanning the
// order.
//
// Two callers produce the snapshot, and it is deliberately the same shape
// for both:
//   • a FLAG carries it (orders_flagged.pack_state, migration 154) so the
//     leader resolving it opens the desk as the packer left it. Only
//     applied to USER-flagged orders — an auto-flagged one already
//     shipped, so its snapshot describes units that have left the
//     building and buildWorkingOrder zeroes those instead.
//   • a REOPEN rebuilds it from the parcels it is deleting
//     (order-reopen.js buildPackState), so reopening a shipped order at
//     the desk gives back the boxes still taped up in front of the
//     operator.
export const applyPackState = (
    order: DispatchOrder,
    snapshot: FlaggedPackState | null | undefined,
): { order: DispatchOrder; boxes: DispatchBox[] } | null => {
    const snapItems = snapshot?.items ?? [];
    const snapBoxes = snapshot?.boxes ?? [];
    if (snapItems.length === 0 && snapBoxes.length === 0) return null;
    const byLine = new Map(snapItems.map(i => [i.line_position, i]));
    const items = order.items.map(item => {
        const snap = item.line_position !== undefined ? byLine.get(item.line_position) : undefined;
        if (!snap) return item;
        const scanned = Math.min(item.quantity, snap.scannedCount || 0);
        const barcodeDamaged = Math.min(item.quantity - scanned, snap.barcodeDamagedCount || 0);
        return {
            ...item,
            scannedCount: scanned,
            barcodeDamagedCount: barcodeDamaged,
            missingCount: snap.missingCount || 0,
            damagedCount: snap.damagedCount || 0,
            wrongItemCount: snap.wrongItemCount || 0,
            assignedBoxes: Array.isArray(snap.assignedBoxes) ? [...snap.assignedBoxes] : [],
            scanStatus: (scanned + barcodeDamaged) >= item.quantity
                ? "scanned" as const : "pending" as const,
        };
    });
    // A snapshot with counts but no boxes (flagged before any box was
    // scanned) still needs the pack screen's default empty Box 1.
    const boxes: DispatchBox[] = snapBoxes.length > 0
        ? snapBoxes.map(b => ({
            id: b.id,
            items: Array.isArray(b.items) ? [...b.items] : [],
            boxRef: b.boxRef ?? null,
            boxName: b.boxName ?? null,
            ...(b.boxLengthMm != null ? { boxLengthMm: b.boxLengthMm } : {}),
            ...(b.boxWidthMm != null ? { boxWidthMm: b.boxWidthMm } : {}),
            ...(b.boxHeightMm != null ? { boxHeightMm: b.boxHeightMm } : {}),
            ...(b.boxWeightGrams != null ? { boxWeightGrams: b.boxWeightGrams } : {}),
            ...(b.measuredWeightGrams != null && b.measuredUnits != null
                ? { measuredWeightGrams: b.measuredWeightGrams, measuredUnits: b.measuredUnits }
                : {}),
        }))
        : [{ id: 1, items: [] }];
    return { order: { ...order, items }, boxes };
};
